// import { HiOutlineXCircle } from 'react-icons/hi';
import React, { useCallback, useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { TextField } from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';

interface InvoicesSearchBarProps {
  onEnterPress: () => void;
  queryString: string;
}

const InvoicesSearchBar = forwardRef<{ getValue: () => string }, InvoicesSearchBarProps>(({ onEnterPress, queryString }, ref) => {
  // Keep local state for the input to prevent parent re-renders on every keystroke
  const [localValue, setLocalValue] = useState(queryString);
  const valueRef = useRef(queryString);

  // Memoize the placeholder to prevent re-evaluation
  const placeholder = useMemo(() => intl.get('searchBar.placeholder'), []);

  // Sync with parent queryString when it changes externally (e.g., after search)
  useEffect(() => {
    setLocalValue(queryString);
    valueRef.current = queryString;
  }, [queryString]);

  // Expose getValue method to parent
  useImperativeHandle(ref, () => ({
    getValue: () => valueRef.current
  }), []);

  const handleChange = useCallback((e) => {
    // Update local state only - no parent re-render!
    const newValue = e.target.value;
    setLocalValue(newValue);
    valueRef.current = newValue;
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      onEnterPress();
    }
  }, [onEnterPress]);

  return (
    <TextField
      size="small"
      placeholder={placeholder}
      aria-label="Search"
      value={localValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
    />
  );
});

InvoicesSearchBar.displayName = 'InvoicesSearchBar';

export default React.memo(InvoicesSearchBar);
